import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildCircularityReport } from "../reports/model/buildCircularityReport";
import { ReportRuntimeUnavailableError, generateHtmlReport } from "../reports/generateHtmlReport";

/**
 * Generates the interactive HTML circularity report for a Product or Project.
 * Everything comes from data already loaded on the Circularity page, so no extra requests are made.
 *
 * @param {object} entity - selected product / project ({ name, sub_category })
 * @param {"Product"|"Project"} topic
 * @param {object} combined_data - output of calculate_combined_data
 * @param {object} pciData - output of computePciData
 * @param {object} boundaryState - Scope & Boundary values
 * @param {number} serviceLife
 */
const useCircularityHtmlReport = ({ entity, topic, combined_data, pciData, boundaryState, serviceLife }) => {
  const { t, i18n } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const generate = useCallback(async () => {
    if (!entity?.id) {
      setError(t(`Select a ${topic.toLowerCase()} before generating the report.`));
      return;
    }
    if (!combined_data?.combined_circularities?.length) {
      setError(t("Add materials before generating the circularity report."));
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      const model = buildCircularityReport({
        entity,
        topic,
        combined_data,
        pciData,
        boundaryState,
        serviceLife,
        translate: t,
        language: i18n.language,
      });
      await generateHtmlReport({ model, translate: t, fileLabel: "Circularity_Report" });
    } catch (err) {
      console.error("Error generating circularity HTML report:", err);
      setError(
        err instanceof ReportRuntimeUnavailableError
          ? t("The report engine is not available on this server. Please contact support.")
          : t("The HTML report could not be generated. Please try again.")
      );
    } finally {
      setIsGenerating(false);
    }
  }, [entity, topic, combined_data, pciData, boundaryState, serviceLife, t, i18n.language]);

  return { generate, isGenerating, error, clearError: () => setError(null) };
};

export default useCircularityHtmlReport;
